/**
 * JSON Web Token decoding for the /jwt tool.
 *
 * Decode only: the signature is never verified, because verifying it needs the
 * issuer's secret or public key and no key should ever be pasted into a web
 * page. Everything here is pure so the page stays wiring, and so the segment
 * and claim handling can be unit-tested without a browser.
 */
import { decodeBase64 } from './base64';

/** A registered claim, resolved into something a human can read. */
export interface JwtClaim {
  name: string;
  /** The raw value as it appears in the payload. */
  value: unknown;
  /** What the claim means (RFC 7519 name, or a common convention). */
  description: string;
  /** Set for the time claims (exp, iat, nbf, auth_time, updated_at). */
  date?: Date;
}

export interface DecodedJwt {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  /** Pretty-printed JSON for the two readable segments. */
  headerJson: string;
  payloadJson: string;
  /** The third segment, undecoded — it is a signature, not data. */
  signature: string;
  /** Registered claims found in the payload, in a stable display order. */
  claims: JwtClaim[];
  issuedAt: Date | null;
  expiresAt: Date | null;
  notBefore: Date | null;
  /** null when the token carries no `exp` claim, so it cannot expire on its own. */
  isExpired: boolean | null;
  /** true while `nbf` is still in the future. */
  isNotYetValid: boolean;
}

/** Thrown for anything that is not a decodable token; the message is user-facing. */
export class JwtError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JwtError';
  }
}

/** RFC 7519 registered claims plus the handful every provider emits anyway. */
const CLAIM_DESCRIPTIONS: Record<string, string> = {
  iss: 'Issuer — who minted the token',
  sub: 'Subject — who or what the token is about',
  aud: 'Audience — who the token is intended for',
  exp: 'Expiration time — the token is rejected from this instant on',
  nbf: 'Not before — the token is rejected until this instant',
  iat: 'Issued at — when the token was minted',
  jti: 'JWT ID — unique identifier for this token',
  azp: 'Authorized party — the client the token was issued to',
  scope: 'Scope — the permissions granted',
  scp: 'Scope — the permissions granted',
  typ: 'Token type',
  auth_time: 'Authentication time — when the subject last authenticated',
  updated_at: 'Profile last updated',
};

/** Claims whose value is seconds since the epoch. */
const TIME_CLAIMS = new Set(['exp', 'nbf', 'iat', 'auth_time', 'updated_at']);

/** Display order: identity, then validity window, then the rest alphabetically. */
const CLAIM_ORDER = ['iss', 'sub', 'aud', 'azp', 'scope', 'scp', 'jti', 'iat', 'nbf', 'exp'];

/** base64url -> text: restore the standard alphabet and the stripped padding. */
export function decodeBase64Url(segment: string): string {
  const normalised = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalised + '='.repeat((4 - (normalised.length % 4)) % 4);
  return decodeBase64(padded);
}

function parseSegment(segment: string, label: string): Record<string, unknown> {
  let json: string;
  try {
    json = decodeBase64Url(segment);
  } catch {
    throw new JwtError(`The ${label} segment is not valid base64url.`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new JwtError(`The ${label} segment does not contain JSON.`);
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new JwtError(`The ${label} segment is not a JSON object.`);
  }
  return parsed as Record<string, unknown>;
}

/** Seconds-since-epoch claim -> Date, or null when the value is not a number. */
function claimDate(value: unknown): Date | null {
  const seconds = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(seconds)) {
    return null;
  }
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
}

function buildClaims(payload: Record<string, unknown>): JwtClaim[] {
  const known = Object.keys(payload).filter((key) => key in CLAIM_DESCRIPTIONS);
  known.sort((a, b) => {
    const ai = CLAIM_ORDER.indexOf(a);
    const bi = CLAIM_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return known.map((name) => {
    const value = payload[name];
    const claim: JwtClaim = {
      name,
      value,
      description: CLAIM_DESCRIPTIONS[name],
    };
    if (TIME_CLAIMS.has(name)) {
      const date = claimDate(value);
      if (date) {
        claim.date = date;
      }
    }
    return claim;
  });
}

/**
 * Split, decode and interpret a JWT.
 *
 * `now` is injectable so the expiry verdict is testable; it defaults to the
 * current clock.
 */
export function decodeJwt(token: string, now: number = Date.now()): DecodedJwt {
  const trimmed = token.trim().replace(/^Bearer\s+/i, '');
  if (!trimmed) {
    throw new JwtError('Paste a token to decode.');
  }

  const segments = trimmed.split('.');
  if (segments.length !== 3) {
    throw new JwtError(
      `A JWT has three dot-separated segments; this value has ${segments.length}.`,
    );
  }

  const header = parseSegment(segments[0], 'header');
  const payload = parseSegment(segments[1], 'payload');

  const expiresAt = 'exp' in payload ? claimDate(payload.exp) : null;
  const notBefore = 'nbf' in payload ? claimDate(payload.nbf) : null;

  return {
    header,
    payload,
    headerJson: JSON.stringify(header, null, 2),
    payloadJson: JSON.stringify(payload, null, 2),
    signature: segments[2],
    claims: buildClaims(payload),
    issuedAt: 'iat' in payload ? claimDate(payload.iat) : null,
    expiresAt,
    notBefore,
    isExpired: expiresAt === null ? null : expiresAt.getTime() <= now,
    isNotYetValid: notBefore !== null && notBefore.getTime() > now,
  };
}
